import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import debounce from 'lodash/debounce';
import flowRight from 'lodash/flowRight';
import * as CONSTANTS from '../constants';
import IncomingLienertApplicantTable from '../incomingLienertApplicant/IncomingLienertApplicantTable';
import TEXT from '../text';
import APPLICANT_TEXT from '../incomingLienertApplicant/text';
import { post } from '../utils/apiUtils';
import withUser from '../utils/withUser';
import * as UTILS from '../utils/utilFunctions';

/**
 * @typedef LienertApplicationUserDTO
 * @property {string}         id
 * @property {string}         title
 * @property {string}         firstName
 * @property {string}         lastName
 * @property {string}         mail
 * @property {string}         phoneNumber
 * @property {string}         birthDate  date as iso string
 * @property {string}         street
 * @property {string}         postalCode
 * @property {string}         city
 * @property {string}         location
 * @property {string}         linkedIn
 * @property {string}         xing
 * @property {string=}        candidateId
 * @property {string=}        incomingCandidateId
 * @property {string}         keycloakId
 */

class LienertIncomingApplicantUser extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      /**
       * @type {LienertApplicationUserDTO[]}
       */
      lienertApplicants: [],
      open: false,
      loading: false,
      selectedApplicantId: null,
      selectedApplicantFirstName: null,
      selectedApplicantLastName: null
    };
    this.handleOnChangeSearch = flowRight(
      debounce(this.handleOnChangeSearch, 500),
      e => e.persist() || e
    );
  }

  /**
   *
   * @param {string=} nameQuery
   * @param {string=} projectId
   */
  getManyIncomingLienertApplicantUser = (nameQuery, projectId) => {
    this.setState({ loading: true });
    this.props.liftUpStateToApp({ showLoadingIndicatorGlobal: true });

    post(CONSTANTS.getManyIncomingLienertApplicantUserURL, {
      nameQuery,
      projectId
    })
      .then(resBody => {
        if (resBody) {
          this.setState({
            lienertApplicants: resBody.applicants
          });
        }
      })
      .catch(() => {
        this.props.handleSnackbarOpen(TEXT.lienetApplicant.errors.getMany);
      })
      .finally(() => {
        this.setState({ loading: false });
        this.props.liftUpStateToApp({ showLoadingIndicatorGlobal: false });
      });
  };

  deleteLienertApplicant = id => {
    post(CONSTANTS.deleteIncomingApplicantUserURL, { keycloakId: id })
      .then(() => {
        const projectIdFromParams = UTILS.getURLParam(this.props.history, 'pid');
        this.getManyIncomingLienertApplicantUser('', projectIdFromParams);
      })
      .catch(() => {
        this.props.handleSnackbarOpen(APPLICANT_TEXT.deleteCandidateProfileDialog.error);
      })
      .finally(() => {
        this.props.handleSnackbarOpen(APPLICANT_TEXT.deleteCandidateProfileDialog.success);
      });
  };

  /**
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  handleOnChangeSearch = e => {
    const queryString = e.target.value;

    if (queryString || queryString === '') {
      this.getManyIncomingLienertApplicantUser(e.target.value);
    }
  };

  handleOnClickApplicant = id => {
    this.props.history.push(`/applicantUser/details?id=${id}`);
  };

  handleDeleteApplicant = () => {
    const { selectedApplicantId } = this.state;

    if (selectedApplicantId) {
      this.deleteLienertApplicant(selectedApplicantId);
      this.setState({ open: false, selectedApplicantId: null, selectedApplicantFirstName: null, selectedApplicantLastName: null });
    }
  };

  handleOpenDialog = (id, firstName, lastName) => {
    this.setState({
      open: true,
      selectedApplicantId: id,
      selectedApplicantFirstName: firstName,
      selectedApplicantLastName: lastName
    });
  };

  componentDidMount() {
    document.title = `Kandidatenprofile - ${UTILS.getTitle()}`;
    const projectIdFromParams = UTILS.getURLParam(this.props.history, 'pid');
    this.getManyIncomingLienertApplicantUser('', projectIdFromParams);
  }

  render() {
    return (
      <div className="container-fluid d-flex flex-column">
        <div className="m-3 w-full d-flex flex-column align-items-center">
          <TextField
            style={{ width: '400px' }}
            disabled={this.state.loading}
            onChange={this.handleOnChangeSearch}
            placeholder={APPLICANT_TEXT.searchField.placeHolder}
            variant="outlined"
          />
        </div>
        <IncomingLienertApplicantTable
          applicantList={this.state.lienertApplicants}
          onApplicantClick={this.handleOnClickApplicant}
          handleSnackbarOpen={this.props.handleSnackbarOpen}
          onDeleteApplicant={this.handleOpenDialog}
        />
        <Dialog
          open={this.state.open}
          onClose={() =>
            this.setState({
              open: false,
              selectedApplicantId: null,
              selectedApplicantFirstName: null,
              selectedApplicantLastName: null
            })
          }
        >
          <DialogTitle>{APPLICANT_TEXT.deleteCandidateProfileDialog.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {APPLICANT_TEXT.deleteCandidateProfileDialog.description}
              <strong>
                {this.state.selectedApplicantFirstName} {this.state.selectedApplicantLastName}
              </strong>
              {APPLICANT_TEXT.deleteCandidateProfileDialog.descriptionEnd}
            </DialogContentText>
            <DialogActions>
              <Button
                onClick={() =>
                  this.setState({
                    open: false,
                    selectedApplicantId: null,
                    selectedApplicantFirstName: null,
                    selectedApplicantLastName: null
                  })
                }
                color="primary"
              >
                {APPLICANT_TEXT.deleteCandidateProfileDialog.cancel}
              </Button>
              <Button color="primary" onClick={this.handleDeleteApplicant}>
                {APPLICANT_TEXT.deleteCandidateProfileDialog.delete}
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
}

LienertIncomingApplicantUser.propTypes = {
  liftUpStateToApp: PropTypes.func.isRequired,
  handleSnackbarOpen: PropTypes.func.isRequired,
  history: PropTypes.any.isRequired
};

export default withUser(LienertIncomingApplicantUser);
